import * as React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import EventBusyOutlined from '@mui/icons-material/EventBusyOutlined'
import FilterAltOffOutlined from '@mui/icons-material/FilterAltOffOutlined'
import { useTheme } from '@mui/material/styles'
import { TOUCH_TARGET_PX } from './touch'

// AC5: an empty-state message when there are no events to show. Two distinct
// cases the design calls out (COPY.md §7 / Q2):
//  - `no-events`   : nothing came back from any source for this range
//  - `filters-off` : events exist but the active filters hide all of them
// plus `load-error` (the whole request failed), so a failure is never
// misreported as an empty calendar.
// Copy is provisional — Product owns the final wording (Q2).
export type CalendarEmptyVariant = 'no-events' | 'filters-off' | 'load-error'

type Props = {
  variant: CalendarEmptyVariant
  /** Renders a "Try again" action (used by `load-error`). */
  onRetry?: () => void
}

const CONTENT: Record<CalendarEmptyVariant, { title: string; body: string }> = {
  'no-events': {
    title: 'No events to show.',
    body: 'There are no events on your calendar for this period.',
  },
  'filters-off': {
    title: 'Your filters are hiding all events.',
    body: 'Turn on a filter above to see events.',
  },
  'load-error': {
    title: "Couldn't load events",
    body: 'Something went wrong loading your calendar.',
  },
}

const ICONS: Record<CalendarEmptyVariant, typeof EventBusyOutlined> = {
  'no-events': EventBusyOutlined,
  'filters-off': FilterAltOffOutlined,
  'load-error': ErrorOutline,
}

export function CalendarEmptyState({ variant, onRetry }: Props) {
  const theme = useTheme()
  const { title, body } = CONTENT[variant]
  const Icon = ICONS[variant]

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1,
        minHeight: 240,
        px: 2,
        py: 6,
        color: theme.palette.text.secondary,
      }}
    >
      <Icon sx={{ fontSize: 48, color: theme.palette.text.disabled }} />
      <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
        {title}
      </Typography>
      <Typography variant="body2">{body}</Typography>
      {onRetry && (
        <Button
          variant="outlined"
          size="small"
          onClick={onRetry}
          data-test-id="calendar-retry"
          // The overlay passes pointer events through to the grid; the action itself must still be clickable.
          sx={{
            mt: 1,
            textTransform: 'none',
            pointerEvents: 'auto',
            minHeight: TOUCH_TARGET_PX,
          }}
        >
          Try again
        </Button>
      )}
    </Box>
  )
}
